import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as Account from "../../account";
import * as api from "../../services/api";
import { PublicComplianceFooter } from "../../shares/PublicSiteChrome";
import TopNavbar from "../../shares/TopNavbar";

const PLATFORM_ONLINE = "quickbooks_online";
const PLATFORM_DESKTOP = "quickbooks_desktop";

function ReconnectQuickBooksCloud() {
    return (
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 text-left shadow-sm">
            <p className="font-semibold text-slate-900">How to reconnect from Fora Connector</p>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-700">
                <li>
                    Open the <Link to="/" className="font-semibold text-slate-900 underline hover:text-slate-700">dashboard</Link>{"."}
                </li>
                <li>
                    Sign in to <strong>Fora Connector</strong> in this browser (if you are not already signed in).
                </li>
                <li>
                    Click <strong>Connect to QuickBooks</strong> and complete Intuit sign-in to authorize access
                    again—the same flow you used the first time.
                </li>
            </ol>
            <p className="mt-4 text-xs text-slate-600">
                You can also start authorization from{" "}
                <Link to="/connect" className="font-semibold text-slate-800 underline hover:text-slate-950">
                    /connect
                </Link>
                .
            </p>
        </div>
    );
}

function ReconnectQuickBooksBrowser() {
    return (
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 text-left shadow-sm">
            <p className="font-semibold text-slate-900">How to reconnect from Fora Connector</p>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-700">
                <li>Open the Fora Connector dashboard and select <strong>QuickBooks</strong>.</li>
                <li>
                    Run the desktop checks, then use <strong>Connect</strong> so this browser links to your QuickBooks
                    company again.
                </li>
                <li>
                    Keep <strong>QuickBooks</strong> and the Fora Connector desktop helper running when your organization
                    uses them.
                </li>
            </ol>
        </div>
    );
}

/**
 * Standalone disconnect: opening this URL performs disconnect when possible (no dashboard required).
 * Other apps can link here, e.g. /disconnect?platform=quickbooks_online
 */
export default function Disconnect() {
    const [searchParams] = useSearchParams();
    const rawPlatform = searchParams.get("platform");
    const p = rawPlatform == null ? "" : String(rawPlatform).trim();
    const invalidPlatform = Boolean(p && p !== PLATFORM_ONLINE && p !== PLATFORM_DESKTOP);
    const isOnline = p === PLATFORM_ONLINE;
    const isDesktop = p === PLATFORM_DESKTOP;
    const hasPlatform = isOnline || isDesktop;

    const [phase, setPhase] = useState(() => (invalidPlatform ? "done" : "running"));
    const [onlineOutcome, setOnlineOutcome] = useState(null);
    const [desktopOutcome, setDesktopOutcome] = useState(null);

    useEffect(() => {
        let cancelled = false;

        if (invalidPlatform) {
            return;
        }

        const doOnline = !p || p === PLATFORM_ONLINE;
        const doDesktop = !p || p === PLATFORM_DESKTOP;

        async function run() {
            let onlineRes = "skipped";
            let desktopRes = "skipped";

            if (doOnline) {
                const acc = Account.getUserAccount();
                if (!acc) {
                    onlineRes = "not_signed_in";
                } else {
                    try {
                        await api.disconnectQuickBooks();
                        onlineRes = "success";
                    } catch {
                        onlineRes = "error";
                    }
                }
            }

            if (doDesktop) {
                try {
                    window.localStorage.setItem(Account.quickBooksDesktopLocalKey, "disconnected");
                    desktopRes = "success";
                } catch {
                    desktopRes = "error";
                }
            }

            if (cancelled) return;
            setOnlineOutcome(onlineRes);
            setDesktopOutcome(desktopRes);
            setPhase("done");
        }

        run();
        return () => {
            cancelled = true;
        };
    }, [invalidPlatform, p]);

    const dashboardTo = hasPlatform
        ? { pathname: "/", search: `?platform=${encodeURIComponent(p)}` }
        : { pathname: "/", search: `?platform=${PLATFORM_DESKTOP}` };

    const learnMoreTo = "/learn-more";

    /** Static copy only (no duplicate progress/outcome UI). */
    function resolveCopy() {
        if (invalidPlatform) {
            return {
                title: "Could not disconnect from this link",
                message: (
                    <>
                        The web address has an unknown <strong>platform</strong> value. Use{" "}
                        <code className="rounded bg-slate-100 px-1 text-sm">?platform=quickbooks_online</code> or{" "}
                        <code className="rounded bg-slate-100 px-1 text-sm">?platform=quickbooks_desktop</code>, or open{" "}
                        <code className="rounded bg-slate-100 px-1 text-sm">/disconnect</code> with no parameters. You
                        can also disconnect from the Fora Connector dashboard.
                    </>
                ),
            };
        }

        if (phase !== "done") {
            return null;
        }

        if (isOnline) {
            if (onlineOutcome === "not_signed_in") {
                return {
                    title: "QuickBooks disconnect — sign in to finish",
                    message: (
                        <>
                            <p>
                                This page is available without signing in. To <strong>revoke</strong> QuickBooks access
                                for Fora Connector, you must be signed in to Fora Connector in this browser so we can
                                call Intuit to invalidate your authorization.
                            </p>
                            <p className="mt-4">
                                Sign in to Fora Connector, open this disconnect link again, or use{" "}
                                <strong>Disconnect</strong> on the dashboard for QuickBooks.
                            </p>
                            <ReconnectQuickBooksCloud />
                        </>
                    ),
                };
            }
            if (onlineOutcome === "error") {
                return {
                    title: "Could not disconnect QuickBooks",
                    message: (
                        <>
                            <p>
                                Fora Connector could not complete the disconnect request. Try again from the dashboard,
                                or try this page again after signing in.
                            </p>
                            <ReconnectQuickBooksCloud />
                        </>
                    ),
                };
            }
            return {
                title: "QuickBooks Disconnected",
                message: (
                    <>
                        <p>
                            <strong>QuickBooks</strong> is disconnected from Fora Connector. Intuit OAuth access for this
                            integration has been <strong>revoked</strong>—stored tokens are no longer valid, and Fora
                            Connector cannot read or update your QuickBooks company until you{" "}
                            <span className="whitespace-nowrap">authorize again.</span>
                        </p>
                        <ReconnectQuickBooksCloud />
                    </>
                ),
            };
        }

        if (isDesktop) {
            if (desktopOutcome === "error") {
                return {
                    title: "Could not update this browser's QuickBooks link",
                    message: (
                        <>
                            <p>
                                Fora Connector could not update this browser. Clear site data for this site or disconnect
                                from the dashboard.
                            </p>
                            <ReconnectQuickBooksBrowser />
                        </>
                    ),
                };
            }
            return {
                title: "QuickBooks Disconnected",
                message: (
                    <>
                        <p>
                            <strong>QuickBooks</strong> is no longer linked to Fora Connector on this browser. Data will
                            not sync until you connect again from the dashboard.
                        </p>
                        <ReconnectQuickBooksBrowser />
                    </>
                ),
            };
        }

        const onlineTried = !p || p === PLATFORM_ONLINE;
        const desktopTried = !p || p === PLATFORM_DESKTOP;

        if (p === "" && onlineTried && desktopTried) {
            if (onlineOutcome === "not_signed_in" && desktopOutcome === "success") {
                return {
                    title: "QuickBooks Disconnected (partial)",
                    message: (
                        <>
                            <p>
                                <strong>This browser:</strong> It will not try to link to QuickBooks again until you
                                reconnect from the dashboard.
                            </p>
                            <p className="mt-4">
                                <strong>Your account:</strong> Intuit authorization for QuickBooks was not revoked because
                                you are not signed in to Fora Connector—sign in and open this page again, or disconnect
                                from the dashboard.
                            </p>
                            <ReconnectQuickBooksCloud />
                            <ReconnectQuickBooksBrowser />
                        </>
                    ),
                };
            }
            if (onlineOutcome === "error" && desktopOutcome === "success") {
                return {
                    title: "QuickBooks Disconnected (partial)",
                    message: (
                        <>
                            <p>
                                <strong>This browser:</strong> It was updated successfully.
                            </p>
                            <p className="mt-4">
                                <strong>Your account:</strong> Disconnect could not be completed automatically—use the
                                dashboard to disconnect and revoke OAuth for QuickBooks.
                            </p>
                            <ReconnectQuickBooksCloud />
                            <ReconnectQuickBooksBrowser />
                        </>
                    ),
                };
            }
            if (onlineOutcome === "success" && desktopOutcome === "error") {
                return {
                    title: "QuickBooks Disconnected (partial)",
                    message: (
                        <>
                            <p>
                                <strong>Your account:</strong> OAuth access for QuickBooks has been revoked; Fora
                                Connector is no longer authorized for your company.
                            </p>
                            <p className="mt-4">
                                <strong>This browser:</strong> It could not be updated—clear site data or use the
                                dashboard.
                            </p>
                            <ReconnectQuickBooksCloud />
                            <ReconnectQuickBooksBrowser />
                        </>
                    ),
                };
            }
            if (onlineOutcome === "error" && desktopOutcome === "error") {
                return {
                    title: "Could not complete disconnect",
                    message: (
                        <>
                            <p>
                                Fora Connector could not finish disconnecting from this page. Use the dashboard to
                                disconnect QuickBooks.
                            </p>
                            <ReconnectQuickBooksCloud />
                            <ReconnectQuickBooksBrowser />
                        </>
                    ),
                };
            }
            if (onlineOutcome === "not_signed_in" && desktopOutcome === "error") {
                return {
                    title: "Could not complete disconnect",
                    message: (
                        <>
                            <p>
                                You are not signed in to Fora Connector in this browser, so Intuit authorization for
                                QuickBooks was not revoked. This browser could not be updated either—use the dashboard.
                            </p>
                            <ReconnectQuickBooksCloud />
                            <ReconnectQuickBooksBrowser />
                        </>
                    ),
                };
            }
            return {
                title: "QuickBooks Disconnected",
                message: (
                    <>
                        <p>
                            Where this page could update your setup, Fora Connector is no longer linked to{" "}
                            <strong>QuickBooks</strong>. OAuth access has been revoked where disconnect succeeded.
                            Customer, product, and order data will not stay in sync until you connect again from the app.
                        </p>
                        <ReconnectQuickBooksCloud />
                        <ReconnectQuickBooksBrowser />
                    </>
                ),
            };
        }

        return {
            title: "QuickBooks Disconnected",
            message: (
                <>
                    <p>
                        Fora Connector is no longer linked to <strong>QuickBooks</strong> until you connect again from
                        the dashboard.
                    </p>
                    <ReconnectQuickBooksCloud />
                    <ReconnectQuickBooksBrowser />
                </>
            ),
        };
    }

    const copy = resolveCopy();
    const showBody = copy !== null;
    const showRunning = phase === "running" && !invalidPlatform;

    if (showRunning) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-gray-900">
                <TopNavbar />
                <main className="container mx-auto px-4 py-12 sm:py-16">
                    <div className="mx-auto max-w-2xl rounded-2xl bg-white p-8 sm:p-10 shadow-lg shadow-slate-200/70 ring-1 ring-slate-200/80">
                        <p className="text-xs font-semibold tracking-wide text-slate-500">QuickBooks</p>
                        <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Disconnect</h1>

                        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-relaxed text-blue-700">
                            <div className="flex items-center gap-3">
                                <svg
                                    className="h-5 w-5 shrink-0 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    aria-hidden
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                                <span>Disconnecting QuickBooks...</span>
                            </div>
                        </div>

                        <p className="mt-6 text-sm text-slate-600">
                            Please wait while we finish disconnecting.
                        </p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900">
            <TopNavbar />
            <div className="flex flex-col items-center px-4 py-12 sm:py-16">
                <div className="w-full max-w-xl text-center">
                    {showBody && (
                        <>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{copy.title}</h1>
                            <div className="mt-4 text-gray-700 leading-relaxed text-left sm:text-center space-y-1">
                                {copy.message}
                            </div>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
                                <Link
                                    to={dashboardTo}
                                    className="inline-flex justify-center rounded-lg bg-slate-800 px-6 py-3 text-base font-bold text-white shadow hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                                >
                                    Back to dashboard
                                </Link>
                            </div>

                            <p className="mt-8 text-sm text-gray-600">
                                <Link
                                    to={learnMoreTo}
                                    className="text-slate-800 font-semibold underline hover:text-slate-950"
                                >
                                    Learn more about Fora and QuickBooks
                                </Link>
                            </p>

                            <p className="mt-10 text-xs text-gray-500">
                                Intuit and QuickBooks are registered trademarks of Intuit Inc. Used with permission.
                            </p>
                        </>
                    )}
                </div>

                <div className="mt-10 w-full max-w-4xl">
                    <PublicComplianceFooter />
                </div>
            </div>
        </div>
    );
}
